import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Subject, skip, takeUntil } from 'rxjs';
import { AppMenuitem } from './app.menuitem';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { MESSAGES_ENABLED } from '../../app.feature-flags';
import { QR_LOGIN_ENABLED } from '../../web-auth/web-auth.flags';
import { AuthService } from '../../service/auth.service';
import { CardV2Gate } from '../../service/card-v2-gate.service';
import { RsaRemovalStatusService } from '../../service/rsa-removal-status.service';
import { AccountModeService } from '../../service/account-mode.service';
import { AccountStatusService } from '../../service/account-status.service';
import { WebTokenBridge } from '../../web-auth/web-token.bridge';
import { OrgMessagingStore } from '../../org/org-messaging.store';

@Component({
    selector: 'app-menu',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule, TranslateModule],
    template: `<ul class="layout-menu">
        <ng-container *ngFor="let item of model; let i = index; trackBy: trackByMenuItem">
            <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true"></li>
            <li *ngIf="item.separator" class="menu-separator"></li>
        </ng-container>
    </ul> `
})
export class AppMenu implements OnInit, OnDestroy {
    model: MenuItem[] = [];
    private translateService = inject(TranslateService);
    private authService = inject(AuthService);
    private cardV2Gate = inject(CardV2Gate);
    private rsaStatus = inject(RsaRemovalStatusService);
    private accountMode = inject(AccountModeService);
    private accountStatus = inject(AccountStatusService);
    private webBridge = inject(WebTokenBridge);
    // Üyelik türü mesajlaşması (2026-10-22): hiçbir türde açık değilse org "Mesajlar" girdisi gizlenir.
    private orgMessaging = inject(OrgMessagingStore);
    // Hesap durumu (/v2/Account/Status) okunamadiysa tasima ogesi eski davranisla (bayraga gore) gorunur.
    private accountStatusFailed = false;
    // Account/Status + Migration/Status bir kez istenir (ent oturumu varken; bkz. loadStatuses).
    private statusesRequested = false;
    private destroy$ = new Subject<void>();

    ngOnInit() {
        this.buildMenu();

        // Türler okunup/kaydedilip mesajlaşma ayarı değişince menü yeniden kurulur (OrgMetaService.reload yazar; ek HTTP yok).
        this.orgMessaging.anyEnabled$.pipe(skip(1), takeUntil(this.destroy$)).subscribe(() => this.buildMenu());

        // Subscribe to language changes to rebuild menu
        this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.buildMenu();
        });

        this.loadStatuses();

        // Hesap modu (kurumsal / organizasyon) cozulunce ya da hesap degisince menu yeniden kurulur.
        // Ent oturumu sonradan açıldıysa ertelenen durum yüklemesi burada yapılır.
        this.accountMode.mode$.pipe(skip(1), takeUntil(this.destroy$)).subscribe(() => {
            this.loadStatuses();
            this.buildMenu();
        });
    }

    /**
     * Tasima sihirbazi ogesi sunucu bayragina (CardV2:RsaRemoval) ve hesap surumune bagli; durumlar gelince menu yeniden kurulur.
     * Ent oturumu (token) yokken yuklenmez: /setup ile kurulan Organizasyon'da yalniz org oturumu vardir; bu ent uclari
     * token'siz 401 alip /auth/login?expired=1 <-> /org dongusunu baslatiyordu (kxv2t SIFA). Bu durumda tasima ogesi
     * gizli kalir (org yuzeyinde anlamsiz); ent oturumu sonradan acilirsa mod degisiminde bir kez yuklenir.
     */
    private loadStatuses(): void {
        if (this.statusesRequested || !this.cardV2Gate.flagOn() || !this.authService.getAccessToken()) return;
        this.statusesRequested = true;
        this.rsaStatus
            .load()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.buildMenu());
        // Tasima ogesi hesap surumune de bagli: v2'ye gecmis hesapta (kalan v1 kart yoksa) gorunmez.
        // Durum gelene kadar oge gizli tutulur; aksi halde bir an gorunup kaybolur (yaris).
        this.accountStatus
            .load()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => this.buildMenu(),
                error: (err: unknown) => {
                    console.warn('Hesap durumu okunamadi; tasima ogesi bayraga gore gosteriliyor', err);
                    this.accountStatusFailed = true;
                    this.buildMenu();
                }
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * NG0956 düzeltmesi (KX-021): buildMenu her dil değişiminde yeni nesne kimlikleriyle
     * bir model üretiyor; kimlik-bazlı takip tüm menüyü DOM'dan silip yeniden kuruyordu.
     * Dilden bağımsız, yapısal kararlı bir anahtar (grubun ilk alt öğesinin rotası, yoksa
     * indeks) döndürerek Angular DOM'u yeniden kullanır, yalnız etiket bağlamalarını günceller.
     */
    trackByMenuItem(index: number, item: MenuItem): string {
        const firstChildLink = item.items?.[0]?.routerLink;
        if (firstChildLink) {
            return Array.isArray(firstChildLink) ? firstChildLink.join('/') : String(firstChildLink);
        }
        if (item.separator) return `separator-${index}`;
        return `item-${index}`;
    }

    /**
     * Tasima sihirbazi ogesi: derleme + sunucu bayragi acik VE hesap henuz tasinmamis (v1) ya da
     * v2'de yarim kalmis tasima (kendi v1 karti duruyor). v2'ye tamamen gecmis hesapta gizli.
     */
    private migrateItemVisible(): boolean {
        if (!this.cardV2Gate.flagOn() || !this.rsaStatus.isOn()) return false;
        const status = this.accountStatus.status();
        if (!status) return this.accountStatusFailed;
        if (status.schemaVersion !== 2) return true;
        return (this.rsaStatus.ownV1Cards() ?? 0) > 0;
    }

    private buildMenu() {
        // A.3: panel QR (ent) oturumunda kullanılamayan öğeler (taşıma, Güvenlik, Anahtar Oluştur) gizlenir.
        const ent = this.webBridge.isEnt();
        this.model = [
            {
                label: this.translateService.instant('COMMON.MENU.HOME'),
                items: [
                    {
                        label: this.translateService.instant('COMMON.MENU.DASHBOARD'),
                        icon: 'pi pi-fw pi-home',
                        routerLink: ['/']
                    },
                    // Q8: şirket hesabını KarteX uygulamasına taşıma (yalnız QR girişi açıkken, şirket modunda)
                    ...(QR_LOGIN_ENABLED && !ent && this.accountMode.mode() === 'corporate' ? [{ label: this.translateService.instant('qr.migrate.menu'), icon: 'pi pi-fw pi-mobile', routerLink: ['/tasi'] }] : [])
                ]
            },
            {
                label: this.translateService.instant('COMMON.MENU.PORTFOLIO'),
                items: [
                    {
                        label: this.translateService.instant('COMMON.MENU.USERS'),
                        icon: 'pi pi-fw pi-users',
                        routerLink: ['/portfolio/users']
                    },
                    {
                        label: this.translateService.instant('COMMON.MENU.CARDS'),
                        icon: 'pi pi-fw pi-id-card',
                        routerLink: ['/portfolio/cards']
                    },
                    {
                        // 126 PAC E4p: tüm paylaşılan kartlar tek tabloda (kart/üye/etkinlik/şehir süzgeci, CSV)
                        label: this.translateService.instant('COMMON.MENU.CONSOLIDATED_CONTACTS'),
                        icon: 'pi pi-fw pi-address-book',
                        routerLink: ['/portfolio/contacts']
                    },
                    // 126 PAC E2p: kurum anketi; anket uçları v2 API'de → bayrak kapalıyken menüde YOK.
                    ...(this.cardV2Gate.flagOn()
                        ? [
                              {
                                  label: this.translateService.instant('COMMON.MENU.COMPANY_POLLS'),
                                  icon: 'pi pi-fw pi-chart-pie',
                                  routerLink: ['/portfolio/company-polls']
                              }
                          ]
                        : [])
                ]
            },
            {
                label: this.translateService.instant('COMMON.MENU.TEMPLATES'),
                items: [
                    {
                        label: this.translateService.instant('COMMON.MENU.CARD_BACKGROUNDS'),
                        icon: 'pi pi-fw pi-id-card',
                        routerLink: ['/templates/backgrounds']
                    },
                    {
                        label: this.translateService.instant('COMMON.MENU.PREDEFINED_FIELDS'),
                        icon: 'pi pi-fw pi-sliders-h',
                        routerLink: ['/templates/predefined-fields']
                    },
                    // Kart v2 sablon matrisi: bayrak kapaliyken menude YOK (prod derlemesi kapali).
                    ...(this.cardV2Gate.flagOn()
                        ? [
                              {
                                  label: this.translateService.instant('COMMON.MENU.CARD_TEMPLATE'),
                                  icon: 'pi pi-fw pi-table',
                                  routerLink: ['/templates/card-template']
                              },
                              {
                                  label: this.translateService.instant('COMMON.MENU.CONSENT_DOCUMENTS'),
                                  icon: 'pi pi-fw pi-file-pdf',
                                  routerLink: ['/templates/consent-documents']
                              },
                              {
                                  label: this.translateService.instant('COMMON.MENU.COMPANY_FORMS'),
                                  icon: 'pi pi-fw pi-list-check',
                                  routerLink: ['/templates/company-forms']
                              }
                          ]
                        : [])
                ]
            },
            {
                label: this.translateService.instant('COMMON.MENU.REPORTS'),
                items: [
                    {
                        label: this.translateService.instant('COMMON.MENU.SCAN_REPORT'),
                        icon: 'pi pi-fw pi-chart-bar',
                        routerLink: ['/reports/scan-report']
                    },
                    // 126 PAC E5: özet pano; okutma/kişi sayıları bayraktan bağımsız → menüde her zaman (K-Ş5-12)
                    {
                        label: this.translateService.instant('COMMON.MENU.SUMMARY_BOARD'),
                        icon: 'pi pi-fw pi-th-large',
                        routerLink: ['/reports/summary-board']
                    }
                ]
            },
            // Mesajlar modülü prod'a hazır değil — MESSAGES_ENABLED ile gizli (bkz. app.feature-flags.ts)
            ...(MESSAGES_ENABLED
                ? [
                      {
                          label: this.translateService.instant('COMMON.MENU.MESSAGES'),
                          items: [
                              {
                                  label: this.translateService.instant('COMMON.MENU.INBOX'),
                                  icon: 'pi pi-fw pi-inbox',
                                  routerLink: ['/pages/messages/inbox']
                              },
                              {
                                  label: this.translateService.instant('COMMON.MENU.SENT'),
                                  icon: 'pi pi-fw pi-send',
                                  routerLink: ['/pages/messages/sent']
                              },
                              {
                                  label: this.translateService.instant('COMMON.MENU.COMPOSE'),
                                  icon: 'pi pi-fw pi-pencil',
                                  routerLink: ['/pages/messages/compose']
                              }
                          ]
                      }
                  ]
                : []),
            {
                label: this.translateService.instant('COMMON.MENU.COMPANY'),
                items: [
                    {
                        label: this.translateService.instant('COMMON.MENU.COMPANY_PROFILE'),
                        icon: 'pi pi-fw pi-building',
                        routerLink: ['/pages/company-profile']
                    },
                    {
                        label: this.translateService.instant('COMMON.MENU.SUBSCRIPTION'),
                        icon: 'pi pi-fw pi-credit-card',
                        routerLink: ['/subscription']
                    },
                    // Yetkililer yalnız QR (ent) oturumunda: Keycloak şirket hesabı önce /tasi ile taşınır; kurum anahtarı yoksa sayfa açılamaz.
                    ...(ent
                        ? this.webBridge.session('ent')?.keys.length
                            ? [
                                  {
                                      label: this.translateService.instant('COMMON.MENU.KEY_HOLDERS'),
                                      icon: 'pi pi-fw pi-user-edit',
                                      routerLink: ['/pages/company-holders']
                                  }
                              ]
                            : []
                        : [
                              {
                                  label: this.translateService.instant('COMMON.MENU.SECURITY'),
                                  icon: 'pi pi-fw pi-shield',
                                  routerLink: ['/info/security']
                              },
                              {
                                  label: this.translateService.instant('COMMON.MENU.GENERATE_KEY'),
                                  icon: 'pi pi-fw pi-key',
                                  routerLink: ['/info/generatekey']
                              }
                          ]),
                    // Kart v2 tasima sihirbazi: bayrak + sunucu bayragi (CardV2:RsaRemoval) acikken ve hesap
                    // tasinmamis ya da yarim kalmisken gorunur (bkz. migrateItemVisible). Sunucu bayragi
                    // kapaliyken tasima hesaba v2 asgari mobil surumunu yazacagi icin oge gizlidir.
                    ...(this.migrateItemVisible()
                        ? [
                              {
                                  label: this.translateService.instant('COMMON.MENU.MIGRATE_V2'),
                                  icon: 'pi pi-fw pi-shield',
                                  routerLink: ['/info/migrate-v2']
                              }
                          ]
                        : [])
                ]
            },
            // KX admin: yalnız kartexadmin realm rolü görür (token'daki realm_access.roles).
            ...(this.authService.isKartexAdmin()
                ? [
                      {
                          label: this.translateService.instant('COMMON.MENU.ADMIN'),
                          items: [
                              {
                                  label: this.translateService.instant('COMMON.MENU.ADMIN_DASHBOARD'),
                                  icon: 'pi pi-fw pi-shield',
                                  routerLink: ['/admin']
                              },
                              {
                                  label: this.translateService.instant('COMMON.MENU.ADMIN_USERS'),
                                  icon: 'pi pi-fw pi-users',
                                  routerLink: ['/admin/users']
                              },
                              {
                                  label: this.translateService.instant('COMMON.MENU.ADMIN_ENTERPRISES'),
                                  icon: 'pi pi-fw pi-building',
                                  routerLink: ['/admin/enterprises']
                              },
                              {
                                  label: this.translateService.instant('COMMON.MENU.ADMIN_ENTERPRISE_USAGE'),
                                  icon: 'pi pi-fw pi-chart-bar',
                                  routerLink: ['/admin/enterprise-usage']
                              },
                              {
                                  label: this.translateService.instant('COMMON.MENU.ADMIN_ENTERPRISE_LEADS'),
                                  icon: 'pi pi-fw pi-inbox',
                                  routerLink: ['/admin/enterprise-leads']
                              },
                              {
                                  label: this.translateService.instant('COMMON.MENU.ADMIN_PARTNERS'),
                                  icon: 'pi pi-fw pi-sitemap',
                                  routerLink: ['/admin/partners']
                              },
                              {
                                  label: this.translateService.instant('COMMON.MENU.ADMIN_SETUP_FUNNEL'),
                                  icon: 'pi pi-fw pi-filter',
                                  routerLink: ['/admin/setup-funnel']
                              },
                              {
                                  label: this.translateService.instant('COMMON.MENU.ADMIN_PLANS'),
                                  icon: 'pi pi-fw pi-sliders-h',
                                  routerLink: ['/admin/plans']
                              },
                              {
                                  label: this.translateService.instant('COMMON.MENU.ADMIN_CAMPAIGNS'),
                                  icon: 'pi pi-fw pi-send',
                                  routerLink: ['/admin/campaigns']
                              },
                              {
                                  label: this.translateService.instant('COMMON.MENU.ADMIN_MODERATION'),
                                  icon: 'pi pi-fw pi-flag',
                                  routerLink: ['/admin/moderation']
                              },
                              {
                                  label: this.translateService.instant('COMMON.MENU.ADMIN_LOGS'),
                                  icon: 'pi pi-fw pi-list',
                                  routerLink: ['/admin/logs']
                              },
                              // Faturalama (B-19 §3.5) — alt menü
                              {
                                  label: this.translateService.instant('COMMON.MENU.ADMIN_BILLING'),
                                  icon: 'pi pi-fw pi-credit-card',
                                  items: [
                                      { label: this.translateService.instant('COMMON.MENU.ADMIN_BILLING_SUBSCRIPTIONS'), icon: 'pi pi-fw pi-list', routerLink: ['/admin/billing/subscriptions'] },
                                      { label: this.translateService.instant('COMMON.MENU.ADMIN_BILLING_USERS'), icon: 'pi pi-fw pi-users', routerLink: ['/admin/billing/users'] },
                                      { label: this.translateService.instant('COMMON.MENU.ADMIN_BILLING_TRIALS'), icon: 'pi pi-fw pi-gift', routerLink: ['/admin/billing/trials'] },
                                      { label: this.translateService.instant('COMMON.MENU.ADMIN_BILLING_PROMO_CODES'), icon: 'pi pi-fw pi-ticket', routerLink: ['/admin/billing/promo-codes'] },
                                      { label: this.translateService.instant('COMMON.MENU.ADMIN_BILLING_PRO_RULES'), icon: 'pi pi-fw pi-star', routerLink: ['/admin/billing/pro-rules'] },
                                      { label: this.translateService.instant('COMMON.MENU.ADMIN_BILLING_QUOTE_REQUESTS'), icon: 'pi pi-fw pi-inbox', routerLink: ['/admin/billing/quote-requests'] },
                                      { label: this.translateService.instant('COMMON.MENU.ADMIN_BILLING_EVENTS'), icon: 'pi pi-fw pi-bolt', routerLink: ['/admin/billing/events'] },
                                      { label: this.translateService.instant('COMMON.MENU.ADMIN_BILLING_SETTINGS'), icon: 'pi pi-fw pi-cog', routerLink: ['/admin/billing/settings'] },
                                      { label: this.translateService.instant('COMMON.MENU.ADMIN_BILLING_DISCOUNT_TEMPLATES'), icon: 'pi pi-fw pi-percentage', routerLink: ['/admin/billing/discount-templates'] },
                                      { label: this.translateService.instant('COMMON.MENU.ADMIN_BILLING_SELLERS'), icon: 'pi pi-fw pi-briefcase', routerLink: ['/admin/billing/sellers'] },
                                      { label: this.translateService.instant('COMMON.MENU.ADMIN_BILLING_PAYOUTS'), icon: 'pi pi-fw pi-wallet', routerLink: ['/admin/billing/payouts'] },
                                      { label: this.translateService.instant('COMMON.MENU.ADMIN_BILLING_INVOICES'), icon: 'pi pi-fw pi-file', routerLink: ['/admin/billing/invoices'] },
                                      { label: this.translateService.instant('COMMON.MENU.ADMIN_BILLING_FX_RATES'), icon: 'pi pi-fw pi-euro', routerLink: ['/admin/billing/fx-rates'] },
                                      { label: this.translateService.instant('COMMON.MENU.ADMIN_BILLING_NFC_ORDERS'), icon: 'pi pi-fw pi-id-card', routerLink: ['/admin/billing/nfc-orders'] }
                                  ]
                              }
                          ]
                      }
                  ]
                : []),
            // Satıcı portalı: kartexseller realm rolü (Faz 5d).
            ...(this.authService.isKartexSeller()
                ? [
                      {
                          label: this.translateService.instant('COMMON.MENU.SELLER'),
                          items: [
                              { label: this.translateService.instant('COMMON.MENU.SELLER_OVERVIEW'), icon: 'pi pi-fw pi-home', routerLink: ['/seller'] },
                              { label: this.translateService.instant('COMMON.MENU.SELLER_CODES'), icon: 'pi pi-fw pi-ticket', routerLink: ['/seller/codes'] },
                              { label: this.translateService.instant('COMMON.MENU.SELLER_REFERRALS'), icon: 'pi pi-fw pi-share-alt', routerLink: ['/seller/referrals'] },
                              { label: this.translateService.instant('COMMON.MENU.SELLER_COMMISSIONS'), icon: 'pi pi-fw pi-percentage', routerLink: ['/seller/commissions'] },
                              { label: this.translateService.instant('COMMON.MENU.SELLER_PAYOUTS'), icon: 'pi pi-fw pi-wallet', routerLink: ['/seller/payouts'] },
                              { label: this.translateService.instant('COMMON.MENU.SELLER_PROFILE'), icon: 'pi pi-fw pi-user', routerLink: ['/seller/profile'] }
                          ]
                      }
                  ]
                : [])
        ];
        // Admin web oturumu (Panel QR scp=admin): şirket uçları bu oturumda 403 döner; yalnız Yönetim/Satıcı rol grupları kalır.
        if (this.webBridge.active() === 'admin') {
            this.model = this.model.filter((g) => isRoleGroup(g));
            return;
        }
        // Org web oturumu (token yok: yalnız org kaydı) ya da Üyelik kurumunun ent oturumu: menü yalnız Organizasyon işleri (plan §2).
        if (this.accountMode.isOrganization() && (ent || !this.authService.getAccessToken())) this.model = this.buildOrgSurfaceMenu(this.model);
        else if (this.accountMode.isOrganization()) this.model = this.buildOrganizationMenu(this.model);
    }

    /** Hiçbir üyelik türünde mesajlaşma açık değilse (ya da türler henüz okunmadıysa) "Mesajlar" girdisi yoktur. */
    private visibleOrgItems(items: OrgMenuEntry[]): OrgMenuEntry[] {
        return this.orgMessaging.anyEnabled() ? items : items.filter((i) => i.label !== 'ORG.MENU.MESSAGES');
    }

    /**
     * Keycloak (eski kurum hesabı) organizasyon menüsü: yalnız bu oturumda açılan /templates öğeleri.
     * /org öğeleri orgSessionGuard'da /org/login'e düştüğü için konmaz; Şirket menüsünde hiç üyelik öğesi yoktur.
     */
    private membershipGroup(): MenuItem {
        const t = (key: string) => this.translateService.instant(key);
        return {
            label: t('COMMON.MENU.MEMBERSHIP'),
            items: [
                { label: t('COMMON.MENU.MEMBERSHIP'), icon: 'pi pi-fw pi-users', routerLink: ['/templates/membership'] },
                { label: t('COMMON.MENU.CONSENT_DOCUMENTS'), icon: 'pi pi-fw pi-file-pdf', routerLink: ['/templates/consent-documents'] }
            ]
        };
    }

    /**
     * Organizasyon oturumu (org web oturumu ya da Üyelik kurumunun ent oturumu): oturum türünün bütün işleri /org altındadır.
     * Şirket, Portföy, Şablonlar, Raporlar, şirket profili, abonelik ve güvenlik girdileri yoktur ("öteki girişle açın" yok).
     * kartexadmin / satıcı grupları rolle gelir.
     */
    private buildOrgSurfaceMenu(full: MenuItem[]): MenuItem[] {
        const t = (key: string) => this.translateService.instant(key);
        return [
            ...ORG_MENU_GROUPS.map((g) => ({ label: t(g.label), items: this.visibleOrgItems(g.items).map((i) => ({ label: t(i.label), icon: i.icon, routerLink: [i.link] })) })),
            ...full.filter((g) => isRoleGroup(g))
        ];
    }

    /**
     * Organizasyon modu, Keycloak oturumu (eski kurum hesabı): yalniz Onay belgeleri, Uyelik ve sirket profili/hesap.
     * Deneme, abonelik, fatura ve kart atama girdileri yoktur; Cikis ust cubuktadir.
     * kartexadmin / satici gruplari rolle gelir, mod onlari degistirmez.
     */
    private buildOrganizationMenu(full: MenuItem[]): MenuItem[] {
        const t = (key: string) => this.translateService.instant(key);
        return [
            this.membershipGroup(),
            {
                label: t('COMMON.MENU.ORGANIZATION'),
                items: [
                    { label: t('COMMON.MENU.ORGANIZATION_PROFILE'), icon: 'pi pi-fw pi-building', routerLink: ['/pages/company-profile'] },
                    // A.3: ent oturumunda Güvenlik gizli.
                    ...(this.webBridge.isEnt() ? [] : [{ label: t('COMMON.MENU.SECURITY'), icon: 'pi pi-fw pi-shield', routerLink: ['/info/security'] }])
                ]
            },
            ...full.filter((g) => isRoleGroup(g))
        ];
    }
}

/** Kurum paneli (/org) girdi tanımları; Org oturumu menüsü (ORG_MENU_GROUPS) bunlardan kurulur. Ayarlar en sonda (E20). */
const ORG_MENU_ITEMS: { label: string; icon: string; link: string }[] = [
    { label: 'ORG.MENU.OVERVIEW', icon: 'pi pi-fw pi-chart-bar', link: '/org' },
    { label: 'ORG.MENU.MEMBERS', icon: 'pi pi-fw pi-id-card', link: '/org/members' },
    // §7: Başvurular ve Onay bekleyenler tek gelen kutusunda (eski rotalar buraya yönlenir).
    { label: 'ORG.MENU.INBOX', icon: 'pi pi-fw pi-inbox', link: '/org/inbox' },
    { label: 'ORG.MENU.CONTENT', icon: 'pi pi-fw pi-megaphone', link: '/org/content' },
    { label: 'ORG.MENU.MESSAGES', icon: 'pi pi-fw pi-comments', link: '/org/messages' },
    { label: 'ORG.MENU.DOCUMENTS', icon: 'pi pi-fw pi-folder', link: '/org/documents' },
    { label: 'ORG.MENU.FORMS', icon: 'pi pi-fw pi-list-check', link: '/org/forms' },
    { label: 'ORG.MENU.TYPES', icon: 'pi pi-fw pi-tags', link: '/org/types' },
    { label: 'ORG.MENU.INVITE', icon: 'pi pi-fw pi-user-plus', link: '/org/invite' },
    { label: 'ORG.MENU.AUDIT', icon: 'pi pi-fw pi-history', link: '/org/audit' },
    { label: 'ORG.MENU.SETTINGS', icon: 'pi pi-fw pi-cog', link: '/org/settings' }
];

type OrgMenuEntry = { label: string; icon: string; link: string };
const orgItem = (key: string): OrgMenuEntry => {
    const e = ORG_MENU_ITEMS.find((i) => i.label === key);
    if (!e) throw new Error(`[app-menu] ORG_MENU_ITEMS içinde yok: ${key}`);
    return e;
};

/** Org oturumu menüsü (plan §2): gruplar ve sıra. Girdi tanımları ORG_MENU_ITEMS ile aynıdır (ikon/rota tek yerde). */
const ORG_MENU_GROUPS: { label: string; items: OrgMenuEntry[] }[] = [
    { label: 'ORG.MENU.GROUP_HOME', items: [orgItem('ORG.MENU.OVERVIEW')] },
    {
        label: 'ORG.MENU.GROUP_MEMBERS',
        items: [orgItem('ORG.MENU.MEMBERS'), orgItem('ORG.MENU.INBOX'), orgItem('ORG.MENU.INVITE'), orgItem('ORG.MENU.DOCUMENTS')]
    },
    {
        label: 'ORG.MENU.GROUP_MEMBERSHIP',
        items: [
            orgItem('ORG.MENU.TYPES'),
            orgItem('ORG.MENU.FORMS'),
            { label: 'ORG.MENU.CONSENT_DOCUMENTS', icon: 'pi pi-fw pi-file-pdf', link: '/org/consent-documents' },
            { label: 'ORG.MENU.MEMBERSHIP_SETTINGS', icon: 'pi pi-fw pi-sliders-h', link: '/org/membership-settings' }
        ]
    },
    { label: 'ORG.MENU.GROUP_COMMS', items: [orgItem('ORG.MENU.CONTENT'), orgItem('ORG.MENU.MESSAGES')] },
    {
        label: 'ORG.MENU.GROUP_ADMIN',
        items: [orgItem('ORG.MENU.SETTINGS'), { label: 'ORG.MENU.DEVICES', icon: 'pi pi-fw pi-desktop', link: '/org/settings/devices' }, orgItem('ORG.MENU.AUDIT')]
    }
];

/** kartexadmin / satici gruplari: ilk ogesi /admin ya da /seller altindadir. */
function isRoleGroup(group: MenuItem): boolean {
    const link = group.items?.[0]?.routerLink;
    const first = Array.isArray(link) ? String(link[0] ?? '') : String(link ?? '');
    return first.startsWith('/admin') || first.startsWith('/seller');
}
